import React, { useEffect, useState } from 'react'

import type { Filter, LogicalAndFilter } from '@cubejs-client/core'
import {
  KeyValueFilterDict,
  MAX_MENU_HEIGHT,
} from 'cube/components/CubeReportFilterRow'
import { isNil } from 'ramda'

import { Flex, Select, SelectOption, Text } from '@driftt/dds'

import {
  getFilterValuesFromKeyValueFilters,
  isCubeFilterKeyValueType,
} from '../CubeReportFilterRow'

import {
  useURLParametersParamKey,
  useURLParametersParamValuesOfKey,
} from './useURLParameters.hook'

interface URLParametersSelectProps {
  filterMember: string
  isDisabled: boolean
  onChange: (
    selectedOptionValue: string[],
    logicalAndFilterValues?: { [key: string]: string }
  ) => void
  selectedURLParameters: Filter
}

/**
 *
 *
 * @param {URLParametersSelectProps} URLParametersSelectProps
 * @returns {JSX.Element} JSX.Element
 */
export const URLParametersSelect = ({
  isDisabled,
  onChange,
  selectedURLParameters, // used to preload the values when the filter already exists
  filterMember,
}: URLParametersSelectProps) => {
  const [selectedKeyParam, setSelectedKeyParam] =
    useState<SelectOption<string>>()

  const [selectedValueParam, setSelectedValueParam] =
    useState<SelectOption<string>>()

  const { paramOptions: paramKeyOptions, isLoading: isParamKeysLoading } =
    useURLParametersParamKey()

  const { paramValuesOptions, isLoading: isParamValuesLoading } =
    useURLParametersParamValuesOfKey(`${selectedKeyParam?.value}`)

  useEffect(() => {
    if (
      !!selectedURLParameters &&
      isCubeFilterKeyValueType(selectedURLParameters)
    ) {
      const { key: selectedKeyValues, value: selectedValueValues } =
        getFilterValuesFromKeyValueFilters(
          selectedURLParameters as LogicalAndFilter,
          filterMember
        )
      setSelectedKeyParam({
        value: selectedKeyValues[0],
        label: selectedKeyValues[0],
      })
      setSelectedValueParam({
        value: selectedValueValues[0],
        label: selectedValueValues[0],
      })
    }
  }, [filterMember, selectedURLParameters])

  const handleCreateFilter = (option) => {
    const selectedOption = option as SelectOption<string>

    if (isNil(selectedOption?.value)) {
      return null
    }

    const { key, value } = KeyValueFilterDict[filterMember]

    setSelectedValueParam(selectedOption)
    onChange([selectedOption?.value], {
      [key]: `${selectedKeyParam?.value}`,
      [value]: `${selectedOption?.value}`,
    })
  }

  return (
    <Flex alignItems="baseline">
      <Select
        isLoading={isParamKeysLoading}
        label="Select the paramKey"
        placeholder="key"
        hideLabel
        value={selectedKeyParam}
        options={paramKeyOptions}
        onChange={(option) => {
          const selectedOption = option as SelectOption<string>
          if (!isNil(selectedOption?.value)) {
            setSelectedKeyParam(selectedOption)
          }
        }}
        isDisabled={isDisabled}
        size="small"
        isClearable={false}
        required
        maxMenuHeight={MAX_MENU_HEIGHT}
      />
      <Text variant="body">=</Text>
      <Select
        isLoading={isParamValuesLoading}
        label="Select the paramValue"
        placeholder="value"
        hideLabel
        value={selectedValueParam}
        options={paramValuesOptions}
        onChange={handleCreateFilter}
        isDisabled={isDisabled}
        size="small"
        isClearable={false}
        required
        maxMenuHeight={MAX_MENU_HEIGHT}
      />
    </Flex>
  )
}
